import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPackageList } from '../../../features';
import useToggle from '../../../hooks/useToggle';
import {
  accountStatus, compareVersions, featureUsageModeConst, filterByName, regionNamesByCountryCode, regionNamesByLanguageCode,
} from '../../../sections/AccountList/accountListConfig';

function useAccountListFilters(props) {
  const {
    accountList,
    featureList,
    setSelectedFilters,
    selectedView,
  } = props;

  const dispatch = useDispatch();
  const [isViewConfigEnabled, setIsViewConfigEnabled] = useToggle();
  const packageList = useSelector((state) => state.packageList?.packageMenuList);

  useEffect(() => {
    dispatch(getPackageList());
  }, []);

  useEffect(() => {
    setIsViewConfigEnabled(false);
  }, [selectedView]);

  // Handle filter change function
  const filterRange = (filterName, value, activeThumb) => {
    if (activeThumb === 0) {
      setSelectedFilters((prevFilters) => ({
        ...prevFilters,
        [filterName]: [Math.min(value[0], prevFilters.range[1]), prevFilters.range[1]],
      }));
    } else if (activeThumb === 1) {
      setSelectedFilters((prevFilters) => ({
        ...prevFilters,
        [filterName]: [prevFilters.range[0], Math.max(value[1], prevFilters.range[0])],
      }));
    }
  };

  const filterHealthScore = (filterName, value, activeThumb) => {
    if (activeThumb === 0) {
      setSelectedFilters((prevFilters) => ({
        ...prevFilters,
        [filterName]: [Math.min(value[0], prevFilters.healthScoreRange[1] - 20), prevFilters.healthScoreRange[1]],
      }));
    } else if (activeThumb === 1) {
      setSelectedFilters((prevFilters) => ({
        ...prevFilters,
        [filterName]: [prevFilters.healthScoreRange[0], Math.max(value[1], prevFilters.healthScoreRange[0] + 20)],
      }));
    }
  };

  const filterFeatureUsage = (filterName, value) => {
    setSelectedFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
      ...(value.length === 0 && { featureUsageMode: featureUsageModeConst.USE }),
    }));
  };

  const filterDefault = (filterName, value) => {
    setSelectedFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
    }));
  };

  const handleFilterChange = useCallback((filterName, value, activeThumb) => {
    switch (filterName) {
      case 'range':
        filterRange(filterName, value, activeThumb);
        break;

      case 'healthScoreRange':
        filterHealthScore(filterName, value, activeThumb);
        break;

      case 'featureUsageFilter':
        filterFeatureUsage(filterName, value);
        break;

      default:
        filterDefault(filterName, value);
    }

    if (!isViewConfigEnabled) setIsViewConfigEnabled(true);
  }, []);

  // Generate dropdown
  const dropdown = useMemo(() => {
    const packageVersionOptions = [];
    const salesforceInstanceOptions = [];
    let packageCountryOptions = [];
    let packageLanguageOptions = [];
    const organisationEditionOptions = [];
    const featureUsageModeOptions = Object.values(featureUsageModeConst);
    const licenceStatusOptions = Object.values(accountStatus);
    const licenceAssignmentOptions = ['Site Wide License', 'Less than 25%', '25% - 50%', '50% - 75%', '75% - 100%'];
    const accountVisibilityOptions = [{ id: 1, value: true, name: 'Included' }, { id: 2, value: false, name: 'Excluded' }];
    const healthScoreTrendOptions = [{ id: 1, value: 'stable', name: 'Stable' }, { id: 2, value: 'rising', name: 'Trending' }, { id: 3, value: 'falling', name: 'Declining' }];
    let featureUsageFilterOptions = [];
    let packageListOptions = [];

    if (packageList) {
      packageListOptions = packageList.filter((item) => item.isEnabled !== false).map((item) => ({
        id: item.packageId,
        name: item.packageLabel,
        value: item.packageId,
      }));
    }

    if (accountList && accountList.length) {
      accountList.forEach((element) => {
        if (element.packageVersions && Array.isArray(element.packageVersions)) {
          element.packageVersions.forEach((version) => {
            if (version && !packageVersionOptions.includes(version)) {
              packageVersionOptions.push(version);
            }
          });
        }
        if (element.salesforceInstance) {
          if (!salesforceInstanceOptions.includes(element.salesforceInstance)) {
            salesforceInstanceOptions.push(element.salesforceInstance);
          }
        }
        if (element.countryCode) {
          if (!packageCountryOptions.includes(element.countryCode)) {
            packageCountryOptions.push({ id: element.countryCode, value: element.countryCode, name: regionNamesByCountryCode.of(element.countryCode) });
          }
        }
        if (element.languageLocale) {
          if (!packageLanguageOptions.includes(element.languageLocale)) {
            packageLanguageOptions.push({ id: element.languageLocale, value: element.languageLocale, name: regionNamesByLanguageCode.of(element.languageLocale.replace('_', '-')) });
          }
        }
        if (element.orgEdition) {
          if (!organisationEditionOptions.includes(element.orgEdition)) {
            organisationEditionOptions.push(element.orgEdition);
          }
        }
      });
    }

    if (featureList && featureList.data && featureList.data.length) {
      featureUsageFilterOptions = featureList.data;
    }

    packageVersionOptions.sort(compareVersions);
    packageCountryOptions = filterByName(packageCountryOptions);
    packageLanguageOptions = filterByName(packageLanguageOptions);

    return {
      accountVisibilityOptions,
      healthScoreTrendOptions,
      licenceAssignmentOptions,
      packageListOptions,
      packageVersionOptions,
      licenceStatusOptions,
      salesforceInstanceOptions,
      packageCountryOptions,
      packageLanguageOptions,
      featureUsageFilterOptions,
      featureUsageModeOptions,
      organisationEditionOptions,
    };
  }, [accountList, featureList, packageList]);

  return {
    handleFilterChange,
    dropdown,
    isViewConfigEnabled,
    setIsViewConfigEnabled,
  };
}

export default useAccountListFilters;
